<script>
    import R, {LOADER} from "../lib/requests";
    import app from "../stores/app";
    import {
        PAYMENT_TYPE,
        PAYMENT_TYPES,
        RESERVATION_STATUS,
        ROUTE_TYPE,
        TRANSFER_STATUS,
        TRANSFER_STATUSES
    } from "../lib/constants";
    import {formatDate, getDate} from "../lib/lib";
    import Notifier from "../stores/notifier";
    import Alert from "../stores/alerts";
    import Modal from '../components/modal/Modal.svelte';
    import PriceControl from "../components/controls/PriceControl.svelte";

    export let reservation_id;

    export let onUpdate = () => {
    }
    export let onClose = () => {
    };

    let reservation = null;
    let department = null;
    let receipt_link = null;
    let status = null;
    let is_modified = false;

    const statuses = [
        'On progress',
        'New',
        'Completed',
        'Failed',
        'Canceled'
    ];

    const channel = new BroadcastChannel('app');

    channel.onmessage = (event) => {
        const data = event.data;
        if (data.type === 'transfer/update') {
            // find route of updated transfer
            const transfer = data.data;
            let route = reservation.routes.find(x => x.transfer_id == transfer.id);
            if (route) {
                ['transfer_at', 'pickup_id', 'pickup_name', 'pickup_address', 'drop_id', 'drop_name', 'drop_address', 'flight_no', 'route_type', 'status_id'].map(field => {
                    route[field] = transfer[field];
                });
                reservation.routes = reservation.routes;
            }
        }
    };

    const onInputChange = () => {
        is_modified = true;
    }

    const load = () => {
        const onSuccess = result => {
            reservation = result;
            department = reservation.department;
            status = ~~reservation.status;

            if (!reservation.viewed_at) {
                $app.new_reservations--;
                if ($app.new_reservations < 0) $app.new_reservations = 0;
            }
        };

        const onFailure = err => {
        };

        R.handle(R.reservation.details, reservation_id, onSuccess, onFailure, LOADER.POPUP);
    }

    const save = (close_on_save = true) => {
        const onSuccess = result => {
            reservation = result;
            Notifier.showMessage("Reservation updated");
            is_modified = false;
            onUpdate(reservation);
            if (close_on_save) {
                close();
            }
        };

        const onFailure = err => {
        };

        reservation.status = status;

        R.handle(R.reservation.edit, reservation, onSuccess, onFailure, LOADER.POPUP);
    }

    const onSendPaymentMail = () => {
        if (!reservation.price) {
            Alert.warning("Reservation", "Set the price for this reservation before sending payment mail!");
            return;
        }

        Alert.confirm("Send payment mail", `This action will ${is_modified ? 'save the reservation and' : ''} send a payment email to customer.\nWould you like to proceed?`, sendPaymentMail);
    }

    const sendPaymentMail = () => {
        const onSuccess = result => {
            Notifier.showMessage("Mail sent successfully to customer");
        };

        const onFailure = err => {
            Alert.warning("Send payment mail", "Could not send a payment email to customer!");
        };

        if (is_modified) {
            save(false);
        }

        R.handle(R.reservation.send_payment_mail, {
            link_code: department.link_code,
            hash: reservation.hash
        }, onSuccess, onFailure, LOADER.POPUP);
    }

    const onCreateTransfer = (route) => {
        Alert.confirm("Create transfer", "This action will create a new transfer using the reservation route details.\nWould you like to proceed?",
            () => createTransfer(route));
    }

    const createTransfer = (route) => {
        const onSuccess = result => {
            route.transfer_id = result.transfer_id;
            route.status_id = TRANSFER_STATUS.OPEN;

            reservation.routes = reservation.routes;

            if (route.transfer_id != null) {
                Notifier.showMessage("Transfer created successfully");
                onUpdate(reservation);
            }
        };

        const onFailure = err => {
            Alert.warning("Create transfer", "Could not create a new transfer from this reservation!");
        };

        R.handle(R.reservation.route_to_transfer, route, onSuccess, onFailure, LOADER.POPUP);
    }

    const onPayOff = () => {
        reservation.payment = reservation.price;
        reservation.payed_at = getDate();
    }


    const close = (param) => {
        reservation = null;

        if (param instanceof Event) {
            onClose();
            return;
        }

        setTimeout(() => {
            onClose();
        }, 200);
    }

    load();

    $: receipt_link = reservation ? `${$app.department_reservation_url}/app/receipt/?ref=${reservation.ref}` : '';
</script>

{#if reservation}
    <Modal modalMd title="Reservation details" on:close="{ close }">
        <div slot="body">
            <div class="h4 mb-2">{reservation.fullname}</div>
            <div class="mb-3">{reservation.phone}, {reservation.email}</div>

            <div class="row">
                <div class="col-md-6">
                    <table class="table table-list mb-3">
                        <tbody>
                        <tr>
                            <td class="px-0 py-2">
                                <div class="small">Department</div>
                                {department?.name}
                            </td>
                        </tr>
                        {#if reservation.agent_email}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Agent Email</div>
                                    {reservation.agent_email}
                                </td>
                            </tr>
                        {/if}
                        <tr>
                            <td class="px-0 py-2">
                                <div class="small">Reference code</div>
                                {reservation.ref || ''}
                            </td>
                        </tr>
                        {#if reservation.order_id}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Order id</div>
                                    {reservation.order_id || ''}
                                </td>
                            </tr>
                        {/if}
                        {#if reservation.transaction_id}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Transaction id</div>
                                    {reservation.transaction_id || ''}
                                </td>
                            </tr>
                        {/if}
                        <tr>
                            <td class="px-0 py-2">
                                <div class="small">Passengers</div>
                                Adults x {reservation.adults}
                                {#if reservation.children > 0}
                                    Children x {reservation.children}
                                {/if}
                                {#if reservation.infants > 0}
                                    Infants x {reservation.infants}
                                {/if}
                            </td>
                        </tr>
                        {#if ~~reservation.baby_seats > 0 || ~~reservation.booster_seats > 0 || ~~reservation.infant_seats}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Extra seats</div>
                                    {#if reservation.baby_seats > 0}
                                        Baby seats x {reservation.baby_seats}
                                    {/if}
                                    {#if reservation.booster_seats > 0}
                                        Boosters x {reservation.booster_seats}
                                    {/if}
                                    {#if reservation.infant_seats > 0}
                                        Infant seats x {reservation.infant_seats}
                                    {/if}
                                </td>
                            </tr>
                        {/if}
                        <tr>
                            <td class="px-0 py-2">
                                <div class="small">Luggage</div>
                                {#if reservation.luggage > 0}
                                    x {reservation.luggage}
                                {:else}
                                    No luggage
                                {/if}
                            </td>
                        </tr>
                        <tr>
                            <td class="px-0 py-2">
                                <div class="small">Vehicle</div>
                                {reservation?.vehicle?.name ?? ''}
                            </td>
                        </tr>
                        <tr>
                            <td class="px-0 py-2">
                                <div class="row">
                                    <div class="col-4">
                                        <div class="small">Price</div>
                                        {#if !reservation.transaction_id}
                                            <PriceControl bind:value={reservation.price} onChange={onInputChange}/>
                                        {:else}
                                            € {reservation.price}
                                        {/if}
                                    </div>
                                    {#if reservation.payment}
                                        <div class="col-4">
                                            <div class="small mb-2">Payment</div>
                                            € {reservation.payment}
                                        </div>
                                    {/if}
                                    <div class="col-4">
                                        <div class="small mb-2">Payment type</div>
                                        {PAYMENT_TYPES[reservation.payment_type]}
                                    </div>
                                </div>
                            </td>
                        </tr>

                        {#if reservation.payment_type == PAYMENT_TYPE.BANK}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="row">
                                        <div class="col-4">
                                            <div class="small">Payment</div>
                                            <PriceControl bind:value={reservation.payment} onChange={onInputChange}/>
                                        </div>
                                        <div class="col-8">
                                            <div class="small">Payment date</div>
                                            <div class="d-flex align-items-center gap-2">
                                                <input type="date" class="form-control"
                                                       bind:value={reservation.payed_at}>
                                                <button class="btn btn-link" on:click={onPayOff}>payoff</button>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        {/if}

                        {#if reservation.notes}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Notes</div>
                                    <div class="text-gray fst-italic">{reservation.notes}</div>
                                </td>
                            </tr>
                        {/if}

                        {#if reservation.payment_type == PAYMENT_TYPE.CARD}
                            <tr>
                                <td class="px-0 py-2">
                                    <div class="small">Receipt</div>
                                    <a href={receipt_link} target="_blank">{receipt_link}</a>
                                </td>
                            </tr>
                        {/if}

                        </tbody>
                    </table>
                </div>
                <div class="col-md-6">
                    <div class="h6 fw-semibold mb-2">Routes</div>
                    <div class="d-flex flex-column gap-2">
                        {#each reservation.routes as route}
                            <div class="route d-flex flex-column gap-2 p-2">
                                {#if route.route_type == ROUTE_TYPE.TOUR}
                                    <div class="d-flex gap-2">
                                        <div class="w-50">
                                            <div class="small text-gray line-height-1">Tour</div>
                                            <div class="fw-semibold">{route.tour_name}</div>
                                        </div>
                                        <div class="w-50">
                                            <div class="small text-gray line-height-1">Pickup</div>
                                            <div class="fw-semibold">{route.pickup_name}</div>
                                        </div>
                                    </div>
                                    <div>
                                        <div class="small text-gray line-height-1">Date</div>
                                        {#if route.transfer_at}
                                            {formatDate(route.transfer_at, 'd-m-y H:i')}
                                        {:else}
                                            <div class="text-danger fw-semibold">Not set yet!</div>
                                        {/if}
                                    </div>
                                {:else}
                                    <div class="d-flex gap-2">
                                        <div class="w-50">
                                            <div class="small text-gray line-height-1">From</div>
                                            <div class="fw-semibold">{route.pickup_name}</div>
                                            {#if route.pickup_address}
                                                <div>{route.pickup_address}</div>
                                            {/if}
                                        </div>
                                        <div class="w-50">
                                            <div class="small text-gray line-height-1">To</div>
                                            <div class="fw-semibold">{route.drop_name}</div>
                                            {#if route.drop_address}
                                                <div>{route.drop_address}</div>
                                            {/if}
                                        </div>
                                    </div>
                                    <div class="d-flex gap-2">
                                        <div class="w-50">
                                            <div class="small text-gray line-height-1">Date</div>
                                            {formatDate(route.transfer_at, 'd-m-y H:i')}
                                        </div>
                                        {#if route.flight_no}
                                            <div class="w-50">
                                                <div class="small text-gray line-height-1">Flight No / Ferry</div>
                                                {route.flight_no}
                                            </div>
                                        {/if}
                                    </div>
                                {/if}

                                {#if route.status_id != null}
                                    <div>
                                        <div class="small text-gray line-height-1">Status</div>
                                        {TRANSFER_STATUSES[route.status_id]}
                                    </div>
                                {/if}

                                {#if !route.transfer_id}
                                    <button class="btn btn-danger d-block w-100 py-1"
                                            on:click={() => onCreateTransfer(route)}>
                                        {#if route.route_type == ROUTE_TYPE.TOUR}
                                            Create tour
                                        {:else}
                                            Create transfer
                                        {/if}
                                    </button>
                                {:else}
                                    {#if route.route_type == ROUTE_TYPE.TOUR}
                                        <a href="/app/tour/{route.transfer_id}" class="btn-link" target="_blank">
                                            Open tour
                                        </a>
                                    {:else}
                                        <a href="/app/transfer/{route.transfer_id}" class="btn-link" target="_blank">
                                            Open transfer
                                        </a>
                                    {/if}
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            </div>

            <div class="row f-flex align-items-end">
                <div class="col-md-6 form-group">
                    <label>Status</label>
                    <select class="form-select" bind:value={status} on:change={onInputChange}>
                        {#each statuses as item, index}
                            <option value="{index}">{item}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-md-6">
                    {#if !reservation.transaction_id && reservation.status != RESERVATION_STATUS.COMPLETED}
                        <button class="btn btn-default d-block w-100 mb-3" on:click={onSendPaymentMail}>
                            SEND PAYMENT MAIL TO CUSTOMER
                        </button>
                    {/if}
                </div>
            </div>
            <hr>
            <div class="text-end">
                <button class="btn btn-link me-2" on:click="{ () => close() }">Close</button>
                <button class="btn btn-primary px-4" on:click={save}>Save</button>
            </div>
        </div>
    </Modal>
{/if}

<style>
    .route {
        border: 1px solid #c6d2d9;
    }
</style>
